import { Key, useCallback } from 'react'

import { useTreeMutations } from '../components/TrackChangesPanel/hooks/TreeMutations/useTreeMutations'
import { DataNode, PartType } from '../types/types'
import * as utils from '../utils'
import {
    useActiveTab,
    useExpandedKeys,
    useWorkspace2Actions,
} from '../Workspace2Store'
import { useNodeMapActions } from '../features/keyOperations'

/** Interface for the useEditorActions hook props. */
interface UseEditorActionsProp {
    /** The current tree data structure */
    treeData: DataNode[]
    /** Function to fetch product family data */
    fetchProductFamily?: (
        partType: PartType,
        rootArticleContentItemIds?: string[]
    ) => void
    /** Utility function to get keys of nodes to collapse */
    withKeysToCollapse: (treeNodes: DataNode[]) => Key[]
    /** Utility function to get keys of nodes to fetch */
    withKeysToFetch: (keysToExpand: string[]) => string[]
    /** The array of Article content item IDs on the Tree */
    memoArticleContentItemIds: string[]
    /** The array of content item IDs on the Tree */
    memoContentItemViewIds: string[]
    /** Flag that indicates whether to show shared property label */
    showSharedPropertyLabel?: boolean
}

/**
 * Custom hook to manage actions in the Workspace Editor.
 * @param {UseEditorActionsProp} UseEditorActionsProp - The hook props.
 * @returns {object} The list of handler functions for the Editor.
 */
export const useEditorActions = ({
    treeData,
    fetchProductFamily,
    withKeysToCollapse,
    withKeysToFetch,
    memoContentItemViewIds,
    showSharedPropertyLabel = false,
}: UseEditorActionsProp) => {
    const activeTab = useActiveTab()
    const expandedKeys = useExpandedKeys()
    const { setExpandedKeys, setArticlesLoading, setTextContentElementToAdd } = useWorkspace2Actions()
    const { reRenderTree } = useTreeMutations()
    const { setSaveOperationInProgress } = useNodeMapActions()

    const handleEditorTabChange = useCallback(
        (tabIndex: number) => {
            // Clear textContentElementToAdd and save operation state when switching tabs
            // This allows add content buttons to work on the new tab
            setTextContentElementToAdd(null)
            setSaveOperationInProgress(false)

            reRenderTree({
                updatedContentItemViewNodes: [],
                tabIndex,
            })
            if (fetchProductFamily && !showSharedPropertyLabel) {
                fetchProductFamily(utils.getActiveTabLabel(tabIndex + 1))
            }
        },
        [reRenderTree, fetchProductFamily, showSharedPropertyLabel, setTextContentElementToAdd, setSaveOperationInProgress]
    )

    const handleExpandAll = useCallback(() => {
        // get all keys that are not expanded yet
        const keysToExpand = memoContentItemViewIds.filter(
            (key) => !expandedKeys.includes(key)
        )
        // find article keys to fetch
        const articleKeysToFetch = withKeysToFetch(keysToExpand)
        // fetch the articles if any
        if (articleKeysToFetch.length > 0) {
            setArticlesLoading(articleKeysToFetch) // to indicate loading
            if (fetchProductFamily) {
                fetchProductFamily(
                    utils.getActiveTabLabel(activeTab),
                    articleKeysToFetch
                )
            }
        }
        // expand all content items
        setExpandedKeys([...expandedKeys, ...keysToExpand])
    }, [memoContentItemViewIds, expandedKeys, fetchProductFamily])

    const handleCollapseAll = useCallback(() => {
        setExpandedKeys(withKeysToCollapse(treeData))
        setArticlesLoading([])
    }, [treeData])

    return {
        handleEditorTabChange,
        handleExpandAll,
        handleCollapseAll,
    }
}
